/**
 * Component overrides for the Intake application
 * Customizes the appearance of MUI components to match the design system
 */

// Using a more generic type since we don't have the MUI types installed
import palette from './palette';
import { spacing } from './spacing';
import { fontWeight, fontSize, lineHeight } from './typography';

// Define a type for components to avoid TypeScript errors
type ComponentsType = Record<string, any>;

const components: ComponentsType = {
  // Button component overrides
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 4,
        padding: `${spacing.sm}px ${spacing.md}px`,
        fontWeight: fontWeight.medium,
        textTransform: 'none',
        transition: 'all 0.2s ease-in-out',
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
        },
      },
      outlined: {
        borderWidth: 1,
        '&:hover': {
          borderWidth: 1,
        },
      },
      // Size variants
      sizeSmall: {
        padding: `${spacing.xs}px ${spacing.sm}px`,
        fontSize: '0.75rem',
      },
      sizeMedium: {
        padding: `${spacing.sm}px ${spacing.md}px`,
        fontSize: '0.875rem',
      },
      sizeLarge: {
        padding: `${spacing.md}px ${spacing.lg}px`,
        fontSize: '1rem',
      },
    },
    variants: [
      // Primary button variants
      {
        props: { variant: 'contained', color: 'primary' },
        style: {
          backgroundColor: palette.primary.main,
          color: palette.primary.contrastText,
          '&:hover': {
            backgroundColor: palette.primary.dark,
          },
        },
      },
      {
        props: { variant: 'outlined', color: 'primary' },
        style: {
          borderColor: palette.primary.main,
          color: palette.primary.main,
          '&:hover': {
            backgroundColor: `${palette.primary.main}10`,
          },
        },
      },
      {
        props: { variant: 'text', color: 'primary' },
        style: {
          color: palette.primary.main,
          '&:hover': {
            backgroundColor: `${palette.primary.main}10`,
          },
        },
      },
      // Secondary button variants
      {
        props: { variant: 'contained', color: 'secondary' },
        style: {
          backgroundColor: palette.secondary.main,
          color: palette.secondary.contrastText,
          '&:hover': {
            backgroundColor: palette.secondary.dark,
          },
        },
      },
      {
        props: { variant: 'outlined', color: 'secondary' },
        style: {
          borderColor: palette.secondary.main,
          color: palette.secondary.main,
          '&:hover': {
            backgroundColor: `${palette.secondary.main}10`,
          },
        },
      },
      {
        props: { variant: 'text', color: 'secondary' },
        style: {
          color: palette.secondary.main,
          '&:hover': {
            backgroundColor: `${palette.secondary.main}10`,
          },
        },
      },
    ],
  },

  // TextField component overrides
  MuiTextField: {
    styleOverrides: {
      root: {
        marginBottom: spacing.md,
      },
    },
  },

  // Input component overrides
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 4,
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.primary.main,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.primary.main,
          borderWidth: 2,
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.status.error.main,
        },
      },
    },
  },

  // InputLabel component overrides
  MuiInputLabel: {
    styleOverrides: {
      root: {
        '&.Mui-focused': {
          color: palette.primary.main,
        },
      },
    },
  },

  // FormHelperText component overrides
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        marginLeft: 0,
        marginTop: spacing.xs,
        fontSize: '0.75rem',
      },
    },
  },

  // Card component overrides
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
      },
    },
  },

  // CardHeader component overrides
  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: spacing.md,
      },
      title: {
        fontSize: '1.25rem',
        fontWeight: fontWeight.medium,
      },
      subheader: {
        fontSize: '0.875rem',
        color: palette.text.secondary,
      },
    },
  },

  // CardContent component overrides
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: spacing.md,
        '&:last-child': {
          paddingBottom: spacing.md,
        },
      },
    },
  },

  // CardActions component overrides
  MuiCardActions: {
    styleOverrides: {
      root: {
        padding: spacing.md,
        justifyContent: 'flex-end',
      },
    },
  },

  // AppBar component overrides
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
        backgroundColor: '#FFFFFF',
        color: palette.text.primary,
      },
    },
  },

  // Toolbar component overrides
  MuiToolbar: {
    styleOverrides: {
      root: {
        minHeight: 64,
        padding: `0 ${spacing.md}px`,
      },
    },
  },

  // Chip component overrides
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        fontSize: '0.75rem',
        height: 24,
      },
      label: {
        paddingLeft: spacing.sm,
        paddingRight: spacing.sm,
      },
      icon: {
        fontSize: '1rem',
      },
      deleteIcon: {
        fontSize: '1rem',
      },
    },
    variants: [
      {
        props: { color: 'primary' },
        style: {
          backgroundColor: `${palette.primary.main}15`,
          color: palette.primary.main,
        },
      },
      {
        props: { color: 'secondary' },
        style: {
          backgroundColor: `${palette.secondary.main}15`,
          color: palette.secondary.main,
        },
      },
      {
        props: { color: 'error' },
        style: {
          backgroundColor: `${palette.status.error.main}15`,
          color: palette.status.error.main,
        },
      },
      {
        props: { color: 'warning' },
        style: {
          backgroundColor: `${palette.status.warning.main}15`,
          color: palette.status.warning.main,
        },
      },
      {
        props: { color: 'info' },
        style: {
          backgroundColor: `${palette.status.info.main}15`,
          color: palette.status.info.main,
        },
      },
      {
        props: { color: 'success' },
        style: {
          backgroundColor: `${palette.status.success.main}15`,
          color: palette.status.success.main,
        },
      },
    ],
  },

  // Alert component overrides
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 4,
        padding: `${spacing.sm}px ${spacing.md}px`,
      },
      icon: {
        fontSize: '1.25rem',
      },
      message: {
        padding: `${spacing.xs}px 0`,
      },
      standardSuccess: {
        backgroundColor: `${palette.status.success.main}15`,
        color: palette.status.success.dark,
      },
      standardInfo: {
        backgroundColor: `${palette.status.info.main}15`,
        color: palette.status.info.dark,
      },
      standardWarning: {
        backgroundColor: `${palette.status.warning.main}15`,
        color: palette.status.warning.dark,
      },
      standardError: {
        backgroundColor: `${palette.status.error.main}15`,
        color: palette.status.error.dark,
      },
    },
  },

  // Table component overrides
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: `${spacing.sm}px ${spacing.md}px`,
        borderBottom: `1px solid ${palette.divider}`,
      },
      head: {
        fontWeight: fontWeight.medium,
        backgroundColor: palette.background.light,
      },
    },
  },

  // Tabs component overrides
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: fontWeight.medium,
        fontSize: '0.875rem',
        minWidth: 100,
        padding: `${spacing.sm}px ${spacing.md}px`,
        '&.Mui-selected': {
          color: palette.primary.main,
        },
      },
    },
  },

  // Tooltip component overrides
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: palette.secondary.main,
        color: palette.secondary.contrastText,
        fontSize: '0.75rem',
        padding: `${spacing.xs}px ${spacing.sm}px`,
        borderRadius: 4,
      },
      arrow: {
        color: palette.secondary.main,
      },
    },
  },

  // Link component overrides
  MuiLink: {
    styleOverrides: {
      root: {
        color: palette.text.link, // Blue 700 (#266DCC)
        fontWeight: fontWeight.regular,
        fontSize: `${fontSize.sm}rem`, // 14px
        lineHeight: lineHeight.normal, // 1.5
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
  },

  // List component overrides
  MuiList: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },

  // List Item component overrides
  MuiListItem: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },

  // List Item Text component overrides
  MuiListItemText: {
    styleOverrides: {
      root: {
        margin: 0,
      },
      primary: {
        fontWeight: fontWeight.regular,
        fontSize: `${fontSize.sm}rem`, // 14px
        lineHeight: lineHeight.normal, // 1.5
      },
    },
  },

  // Typography component overrides
  MuiTypography: {
    styleOverrides: {
      root: {
        '& ul': {
          marginTop: 4, // Additional space between first bullet and content above
          marginBottom: 0,
          paddingLeft: 20,
          '& li': {
            fontWeight: fontWeight.regular,
            fontSize: `${fontSize.sm}rem`, // 14px
            lineHeight: lineHeight.normal, // 1.5
            marginBottom: 6, // Space between list items
            '&:last-child': {
              marginBottom: 0,
            },
          },
        },
      },
    },
    variants: [
      {
        props: { variant: 'label-caps' },
        style: {
          fontWeight: fontWeight.semiBold,
          fontSize: `${fontSize.sm}rem`, // 14px
          lineHeight: lineHeight.tight, // 1.2
          textTransform: 'uppercase',
          letterSpacing: '0.08em', // 8%
        },
      },
      {
        props: { variant: 'label-caps-small' },
        style: {
          fontWeight: fontWeight.regular,
          fontSize: `${fontSize.xs}rem`, // 12px
          lineHeight: lineHeight.tight, // 1.2
          textTransform: 'uppercase',
          letterSpacing: '0.08em', // 8%
        },
      },
      {
        props: { variant: 'product-title' },
        style: {
          fontWeight: fontWeight.bold,
          fontSize: `${fontSize.md}rem`, // 16px
          lineHeight: lineHeight.normal, // 1.5
        },
      },
    ],
  },

  // Paper component overrides
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },

  // CircularProgress component overrides
  MuiCircularProgress: {
    styleOverrides: {
      root: {
        color: palette.primary.main,
      },
    },
  },
};

export default components;
